import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
  type UseInteractionsReturn,
  type UseFloatingData,
} from '@floating-ui/react';
import {
  cloneElement,
  createContext,
  forwardRef,
  HTMLProps,
  isValidElement,
  MutableRefObject,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

type ContextType = {
  open: boolean;
  setOpen: (open: boolean) => void;
  arrowRef: MutableRefObject<null>;
} & UseInteractionsReturn &
  UseFloatingData &
  ReturnType<typeof useTransitionStyles>;

type TooltipOptions = {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isAvailable?: boolean;
};

export function useTooltip({
  initialOpen = false,
  placement = 'top',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  isAvailable,
}: TooltipOptions = {}): ContextType {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = useRef(null);
  const data = useFloating({
    placement,
    open,
    onOpenChange: (open) => !isAvailable && setOpen(open),
    whileElementsMounted: autoUpdate,

    middleware: [
      offset(10),
      flip(),
      shift({ padding: 20 }),
      arrow({ element: arrowRef }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);
  const transitionStyles = useTransitionStyles(context, {
    common: {
      transformOrigin: 'top',
    },
    initial: {
      opacity: 0,
    },
    open: {
      opacity: 1,
    },
    duration: 100,
  });

  const hook = useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      ...interactions,
      ...data,
      ...transitionStyles,
    }),
    [open, setOpen, interactions, transitionStyles, data, arrowRef],
  );
  return hook;
}

const TooltipContext = createContext<ContextType | null>(null);

export const useTooltipContext = (): ContextType => {
  const context = useContext(TooltipContext);
  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }
  return context;
};

export function Tooltip({
  children,
  ...options
}: { children: React.ReactNode } & TooltipOptions) {
  const tooltip = useTooltip(options);

  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
}

export const TooltipTrigger = forwardRef<
  HTMLElement,
  HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
  const context = useTooltipContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  }

  return (
    <button
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

export const TooltipContent = forwardRef<
  HTMLDivElement,
  HTMLProps<HTMLDivElement>
>(function TooltipContent({ children, style, ...props }, propRef) {
  const context = useTooltipContext();
  const ref = useMergeRefs([
    context.refs.setFloating,
    context.arrowRef,
    propRef,
  ]);

  if (!context.open) return null;

  return (
    <FloatingPortal>
      <div
        ref={ref}
        style={{
          ...context.styles,
          ...context.floatingStyles,
          ...style,
        }}
        {...context.getFloatingProps(props)}
      >
        {children}
        <FloatingArrow
          ref={context.arrowRef}
          context={context.context}
          className="fill-white"
          tipRadius={2}
          height={8}
        />
      </div>
    </FloatingPortal>
  );
});
